import { useContext, type FC } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { getCoreLevel } from '@wix/da-shared-react/pkg/publicSession/selectors';
import UserSymbol from '@wix/da-shared-react/pkg/User/_partials/UserSymbol/UserSymbol';
import CoreSymbol from '@wix/da-ds/pkg/Indicators/CoreSymbol';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';

import s from './CoreUpsell.scss';

export interface Props {}

const CoreUpsell: FC<Props> = () => {
  const { t } = useTranslation();
  const { hasCoreMembership } = useContext(SessionContext);
  const coreLevel = useSelector(getCoreLevel);

  return (
    <div className={s['root']}>
      <div className={s['alignment']}>
        {!hasCoreMembership && (
          <>
            <div className={s['upsell-part']}>
              {t('studio.videoCreator.coreUpsell.firstPart.nonCore')}
            </div>
            <div className={s['upsell-part']}>
              {t('studio.videoCreator.coreUpsell.secondPart.nonCore', {
                postProcess: 'reactPostprocessor',
                coreSymbol: (
                  <CoreSymbol
                    className={s['core-icon']}
                    size={IconSize.SMALL}
                  />
                ),
                upgradeLink: (
                  <PlainButtonOrLink
                    className={s['upgrade-link']}
                    href={Url.coreMembershipLink()}
                    target="_blank"
                  >
                    {t('studio.videoCreator.button.upgradeCore')}
                  </PlainButtonOrLink>
                ),
              })}
            </div>
          </>
        )}

        {coreLevel === 'core_plus' && (
          <>
            {t('studio.videoCreator.coreUpsell.firstPart.corePlus', {
              postProcess: 'reactPostprocessor',
              coreIcon: (
                <UserSymbol
                  userSymbol={coreLevel}
                  className={s['core-status']}
                />
              ),
            })}
            <br />
            {t('studio.videoCreator.coreUpsell.secondPart.corePlus', {
              postProcess: 'reactPostprocessor',
              coreSymbol: (
                <CoreSymbol className={s['core-icon']} size={IconSize.SMALL} />
              ),
              upgradeLink: (
                <PlainButtonOrLink
                  className={s['upgrade-link']}
                  href={Url.coreMembershipLink()}
                  target="_blank"
                >
                  {t('studio.videoCreator.coreUpsell.upgradeLink.core')}
                </PlainButtonOrLink>
              ),
            })}
          </>
        )}
        {(coreLevel === 'core_pro' || coreLevel === 'core_pro_plus') && (
          <>
            {t('studio.videoCreator.coreUpsell.firstPart.pro', {
              postProcess: 'reactPostprocessor',
              coreIcon: (
                <UserSymbol
                  userSymbol={coreLevel}
                  className={s['core-status']}
                />
              ),
              percentage: coreLevel === 'core_pro' ? 20 : 40,
            })}
            <br />
            {t('studio.videoCreator.coreUpsell.secondPart.pro', {
              postProcess: 'reactPostprocessor',
              coreSymbol: (
                <CoreSymbol className={s['core-icon']} size={IconSize.SMALL} />
              ),
              upgradeLink: (
                <PlainButtonOrLink
                  className={s['upgrade-link']}
                  href={Url.coreMembershipLink()}
                  target="_blank"
                >
                  {t('studio.videoCreator.coreUpsell.upgradeLink.core')}
                </PlainButtonOrLink>
              ),
            })}
          </>
        )}
        {coreLevel === 'core_max' &&
          t('studio.videoCreator.coreUpsell.firstPart.max', {
            postProcess: 'reactPostprocessor',
            coreIcon: (
              <UserSymbol userSymbol={coreLevel} className={s['core-status']} />
            ),
          })}
      </div>
    </div>
  );
};

CoreUpsell.displayName = 'CoreUpsell';
export default CoreUpsell;
